import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { ProjectStatus, RiskRating, Recommendation } from '@/types'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// ─── Currency & Number Formatting ─────────────────────────────────────────────

export function formatCurrency(value: number, currency = 'BRL'): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(value)
}

export function formatCurrencyUSD(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value)
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('pt-BR').format(value)
}

export function formatPercent(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`
}

export function formatArea(hectares: number): string {
  return `${formatNumber(hectares)} ha`
}

// ─── Date Formatting ──────────────────────────────────────────────────────────

export function formatDate(dateStr: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(dateStr))
}

export function formatDateTime(dateStr: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(dateStr))
}

export function formatRelativeTime(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'agora'
  if (diffMins < 60) return `há ${diffMins}min`
  if (diffHours < 24) return `há ${diffHours}h`
  if (diffDays < 7) return `há ${diffDays}d`
  return formatDate(dateStr)
}

// ─── Status Helpers ───────────────────────────────────────────────────────────

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  DRAFT:     'Rascunho',
  PENDING:   'Pendente',
  ANALYZING: 'Em Análise',
  COMPLETED: 'Concluído',
  APPROVED:  'Aprovado',
  REJECTED:  'Reprovado',
  CANCELLED: 'Cancelado',
}

export const PROJECT_STATUS_COLORS: Record<ProjectStatus, string> = {
  DRAFT:     'bg-gray-100 text-gray-700',
  PENDING:   'bg-yellow-100 text-yellow-800',
  ANALYZING: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-purple-100 text-purple-800',
  APPROVED:  'bg-green-100 text-green-800',
  REJECTED:  'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-500',
}

export function getStatusBadgeClass(status: ProjectStatus): string {
  return PROJECT_STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-700'
}

export function getStatusLabel(status: ProjectStatus): string {
  return PROJECT_STATUS_LABELS[status] ?? status
}

// ─── Risk Score Helpers ───────────────────────────────────────────────────────

export const RISK_RATING_COLORS: Record<RiskRating, string> = {
  AAA: '#15803d',
  AA:  '#16a34a',
  A:   '#22c55e',
  BBB: '#84cc16',
  BB:  '#eab308',
  B:   '#f97316',
  CCC: '#ef4444',
  CC:  '#dc2626',
  C:   '#b91c1c',
  D:   '#7f1d1d',
}

export const RECOMMENDATION_COLORS: Record<Recommendation, string> = {
  APROVAR:  'bg-green-100 text-green-800 border-green-200',
  ANALISAR: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  REJEITAR: 'bg-red-100 text-red-800 border-red-200',
}

export function getRatingColor(rating: RiskRating): string {
  return RISK_RATING_COLORS[rating] ?? '#6b7280'
}

// ─── Document Helpers ──────────────────────────────────────────────────────────

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

export function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toUpperCase() ?? ''
}

// ─── CEP / Document Masks ─────────────────────────────────────────────────────

export function maskCPF(value: string): string {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
    .slice(0, 14)
}

export function maskCNPJ(value: string): string {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
    .slice(0, 18)
}

export function maskPhone(value: string): string {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{4,5})(\d{4})$/, '$1-$2')
    .slice(0, 15)
}

export function maskCEP(value: string): string {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .slice(0, 9)
}

// ─── Misc ─────────────────────────────────────────────────────────────────────

export function truncate(str: string, maxLen: number): string {
  if (str.length <= maxLen) return str
  return str.slice(0, maxLen) + '...'
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
